/**
 * Screenshot Helper Utility
 *
 * Provides functionality to capture screenshots of the current page
 * using html2canvas for feedback/bug reports.
 */

import html2canvas from 'html2canvas';

/**
 * Capture a screenshot of the current page
 *
 * @param {Object} options - html2canvas options
 * @param {number} options.scale - Scale factor for the screenshot (default: 0.8)
 * @param {number} options.maxWidth - Maximum width in pixels (default: 1920)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.85)
 * @returns {Promise<string>} Base64 encoded screenshot data URL
 */
export const captureScreenshot = async (options = {}) => {
  const {
    scale = 0.8,
    maxWidth = 1920,
    quality = 0.85
  } = options;

  try {
    // Hide any elements that shouldn't be in the screenshot
    const elementsToHide = document.querySelectorAll('[data-no-screenshot]');
    elementsToHide.forEach(el => {
      el.dataset.originalDisplay = el.style.display;
      el.style.display = 'none';
    });

    // Capture the screenshot
    const canvas = await html2canvas(document.body, {
      scale,
      logging: false,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      windowWidth: document.documentElement.scrollWidth,
      windowHeight: document.documentElement.scrollHeight,
      scrollX: 0,
      scrollY: 0,
      onclone: (clonedDoc) => {
        // Remove scrollbars from cloned document
        const clonedBody = clonedDoc.body;
        clonedBody.style.overflow = 'hidden';
      }
    });

    // Restore hidden elements
    elementsToHide.forEach(el => {
      el.style.display = el.dataset.originalDisplay || '';
      delete el.dataset.originalDisplay;
    });

    // Resize if too wide
    let finalCanvas = canvas;
    if (canvas.width > maxWidth) {
      const scaleFactor = maxWidth / canvas.width;
      const resizedCanvas = document.createElement('canvas');
      resizedCanvas.width = maxWidth;
      resizedCanvas.height = canvas.height * scaleFactor;

      const ctx = resizedCanvas.getContext('2d');
      ctx.drawImage(canvas, 0, 0, resizedCanvas.width, resizedCanvas.height);

      finalCanvas = resizedCanvas;
    }

    // Convert to data URL (PNG for better quality, but larger size)
    const dataUrl = finalCanvas.toDataURL('image/png', quality);

    // Check size and compress if needed (target max 2MB)
    const sizeInMB = (dataUrl.length * 0.75) / (1024 * 1024);
    if (sizeInMB > 2) {
      // Try JPEG with lower quality
      return finalCanvas.toDataURL('image/jpeg', 0.7);
    }

    return dataUrl;

  } catch (error) {
    console.error('Error capturing screenshot:', error);
    throw new Error('Screenshot konnte nicht erstellt werden');
  }
};

/**
 * Estimate the size of a base64 data URL in MB
 *
 * @param {string} dataUrl - Base64 data URL
 * @returns {number} Size in megabytes
 */
export const getScreenshotSize = (dataUrl) => {
  if (!dataUrl) return 0;
  return (dataUrl.length * 0.75) / (1024 * 1024);
};

/**
 * Check if screenshot capture is supported in the current browser
 *
 * @returns {boolean} True if supported
 */
export const isScreenshotSupported = () => {
  // html2canvas requires canvas support
  const canvas = document.createElement('canvas');
  return !!(canvas.getContext && canvas.getContext('2d'));
};

/**
 * Capture screenshot of a specific element
 *
 * @param {HTMLElement} element - Element to capture
 * @param {Object} options - html2canvas options
 * @returns {Promise<string>} Base64 encoded screenshot data URL
 */
export const captureElementScreenshot = async (element, options = {}) => {
  const {
    scale = 0.8,
    quality = 0.85
  } = options;

  try {
    const canvas = await html2canvas(element, {
      scale,
      logging: false,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff'
    });

    return canvas.toDataURL('image/png', quality);

  } catch (error) {
    console.error('Error capturing element screenshot:', error);
    throw new Error('Screenshot konnte nicht erstellt werden');
  }
};
